import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  Paper,
  Slider,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import type { Swagpack } from "../../../../models/Swagstore/Packs";
import type { PSDTemplate } from "../../../../models/MockupConfig";
import { useLogoPlacement } from "../../../pages/mockup-generator/useLogoPlacement";
import { useMockupEditorPreviewUrl } from "../../../pages/mockup-generator/useMockupEditorPreviewUrl";
import { useStores } from "../../../../use-stores";
import AppButton from "../../common/buttons/AppButton";
import AppDropDown from "../../common/filter/AppDropDown";
import CustomTypography from "../../common/form/CustomTypography";
import FormTitle from "../../common/form/FormTitle";
import ImageUpload from "../../common/upload/ImageUpload";
import { observer } from "mobx-react";

const DISPLAY_MAX_WIDTH = 600;
const DISPLAY_MAX_HEIGHT = 450;

function sortPsdTemplates(
  templates: PSDTemplate[],
  placementTypes: { id: number; sort_order?: number }[],
) {
  return [...templates].sort((a, b) => {
    const ptA = placementTypes.find((pt) => pt.id === (a.placement_type_id ?? 0));
    const ptB = placementTypes.find((pt) => pt.id === (b.placement_type_id ?? 0));
    const orderA = ptA?.sort_order ?? 99;
    const orderB = ptB?.sort_order ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return (a.view_angle ?? "").localeCompare(b.view_angle ?? "");
  });
}

type MockupSize = "large" | "medium" | "small";

function pickPreferredMockupSize(): MockupSize {
  const conn = (navigator as any)?.connection;
  const effectiveType = conn?.effectiveType as string | undefined;
  if (effectiveType === "4g") return "large";
  if (effectiveType === "3g") return "medium";
  if (effectiveType === "2g" || effectiveType === "slow-2g") return "small";
  return "medium";
}

function deriveMockupOutputsFromStoredUrl(url: string): {
  large: string;
  medium: string;
  small: string;
} {
  const match = url.match(/\/(large|medium|small)\.webp$/i);
  if (!match) return { large: url, medium: url, small: url };
  const base = url.replace(/\/(large|medium|small)\.webp$/i, "");
  return {
    large: `${base}/large.webp`,
    medium: `${base}/medium.webp`,
    small: `${base}/small.webp`,
  };
}

const MOCKUP_EXPORT_ZOOM_SCALE = 1.65;
const EXPORT_CARD_PREVIEW_HEIGHT = 180;
const GENERATOR_MODAL_MIN_HEIGHT = 760;

/** Zoom follows pointer: transform-origin tracks cursor inside the clip area. */
function MockupExportPreview({
  src,
  alt,
  fixedHeight,
}: {
  src: string;
  alt: string;
  fixedHeight?: number;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);

  const applyOrigin = useCallback((wrap: HTMLDivElement, clientX: number, clientY: number) => {
    const r = wrap.getBoundingClientRect();
    const w = r.width || 1;
    const h = r.height || 1;
    const x = Math.min(100, Math.max(0, ((clientX - r.left) / w) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - r.top) / h) * 100));
    const el = imgRef.current;
    if (el) el.style.transformOrigin = `${x}% ${y}%`;
  }, []);

  const handlePointerEnter = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => applyOrigin(e.currentTarget, e.clientX, e.clientY),
    [applyOrigin],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => applyOrigin(e.currentTarget, e.clientX, e.clientY),
    [applyOrigin],
  );

  const handlePointerLeave = useCallback(() => {
    const el = imgRef.current;
    if (el) el.style.transformOrigin = "50% 50%";
  }, []);

  return (
    <Box
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      sx={{
        overflow: "hidden",
        borderRadius: 1,
        mb: 1,
        cursor: "zoom-in",
        touchAction: "none",
        height: fixedHeight ?? "auto",
        "&:hover img": { transform: `scale(${MOCKUP_EXPORT_ZOOM_SCALE})` },
      }}
    >
      <Box
        ref={imgRef}
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: "100%",
          height: fixedHeight ?? "auto",
          display: "block",
          borderRadius: 1,
          transition: "transform 0.22s ease-out",
          transformOrigin: "50% 50%",
          objectFit: "contain",
        }}
      />
    </Box>
  );
}

function EmptyStatePanel({
  message,
  title = "Nothing here yet",
  minHeight = 420,
}: {
  message: string;
  title?: string;
  minHeight?: number;
}) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2.5,
        minHeight,
        ...(minHeight > 0
          ? { transform: { xs: "translateY(-14px)", md: "translateY(-24px)" } }
          : {}),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        bgcolor: "transparent",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 3,
          p: 3,
          border: "1px solid",
          borderColor: "rgba(99, 102, 241, 0.18)",
          background:
            "linear-gradient(180deg, rgba(99,102,241,0.08) 0%, rgba(236,72,153,0.06) 100%)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            mx: "auto",
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.8)",
            border: "1px solid rgba(99, 102, 241, 0.22)",
          }}
        >
          <ImageNotSupportedOutlinedIcon sx={{ fontSize: 28, color: "text.secondary" }} />
        </Box>
        <CustomTypography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
          {title}
        </CustomTypography>
        <CustomTypography variant="caption" color="text.secondary">
          {message}
        </CustomTypography>
      </Box>
    </Box>
  );
}

interface MockupGeneratorModalProps {
  open: boolean;
  product: Swagpack | null;
  onClose: () => void;
  initialTab?: "generate" | "exports" | "upload";
  placementTypes: { id: number; sort_order?: number }[];
  psdTemplates: PSDTemplate[];
  selectedPsdId: number | null;
  loadingPsd: boolean;
  savedMockups: Array<{
    id: number;
    reference_id: string | null;
    mockup_url: string;
    created_at: string;
  }>;
  mockupOutputs: { large: string; medium: string; small: string } | null;
  mockupReferenceId: string | null;
  generatingMockup: boolean;
  updatingMockupId?: number | null;
  deletingGeneratedMockupId: number | null;
  lockPsdSelection?: boolean;
  editMode?: boolean;
  editMockupId?: number | null;
  hideGeneratedMockupsTab?: boolean;
  onSelectPsdId: (id: number | null) => void;
  onGenerateMockup: (payload: {
    psd_template_id: number;
    logoFile?: File | null;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }) => void | Promise<void>;
  uploadedLogos: Array<{ id: number; logo_url: string }>;
  loadingUploadedLogos: boolean;
  onUploadLogoToLibrary: (file: File) => void;
  onDeleteUploadedLogo: (id: number) => Promise<void>;
  onDeleteMockup: (id: number) => Promise<void>;
  onManualUploadMockup?: (payload: { id: number; file: File }) => Promise<void>;
  onUploadMockupFiles?: (files: File[]) => Promise<void>;
  onError: (title: string, message?: string) => void;
  embedded?: boolean;
  hideInternalTabs?: boolean;
  controlledTab?: "generate" | "upload" | "exports";
}

const MockupGeneratorModal: React.FC<MockupGeneratorModalProps> = ({
  open,
  product,
  onClose,
  initialTab = "generate",
  placementTypes,
  psdTemplates,
  selectedPsdId,
  loadingPsd,
  savedMockups,
  mockupOutputs,
  mockupReferenceId,
  generatingMockup,
  updatingMockupId = null,
  deletingGeneratedMockupId,
  lockPsdSelection = false,
  editMode = false,
  editMockupId = null,
  hideGeneratedMockupsTab = false,
  onSelectPsdId,
  onGenerateMockup,
  uploadedLogos,
  loadingUploadedLogos,
  onUploadLogoToLibrary,
  onDeleteUploadedLogo,
  onDeleteMockup,
  onManualUploadMockup,
  onUploadMockupFiles,
  onError,
  embedded = false,
  hideInternalTabs = false,
  controlledTab,
}) => {
  const logoLibraryInputRef = React.useRef<HTMLInputElement>(null);
  const shouldAutoSwitchToExportsRef = React.useRef(false);
  const [editorZoom, setEditorZoom] = useState(1);
  const [activeTab, setActiveTab] = useState<"generate" | "exports" | "upload">(initialTab);
  const [manualUploadFiles, setManualUploadFiles] = useState<File[]>([]);
  const preferredSize = useMemo(() => pickPreferredMockupSize(), []);
  const sortedPsdTemplates = sortPsdTemplates(psdTemplates, placementTypes);
  const selectedPsdTemplate = sortedPsdTemplates.find((p) => p.id === selectedPsdId);
  const { mockupConfigStore } = useStores();

  const logoPlacement = useLogoPlacement({
    selectedPsdId,
    zoom: editorZoom,
    psdTemplate: selectedPsdTemplate ?? null,
    onError,
  });

  const handleBlankPreviewResolved = useCallback(
    (templateId: number, url: string) => {
      mockupConfigStore.setPsdTemplateBlankPreviewUrl(templateId, url);
    },
    [mockupConfigStore],
  );

  const { previewImageUrl, loadingPlacementPreview } = useMockupEditorPreviewUrl({
    template: selectedPsdTemplate,
    logoUrl: logoPlacement.logoUrl,
    onBlankPreviewResolved: handleBlankPreviewResolved,
  });

  useEffect(() => {
    logoPlacement.handleLogoUrl(null);
    setEditorZoom(1);
    setActiveTab(editMode ? "upload" : initialTab);
    setManualUploadFiles([]);
    shouldAutoSwitchToExportsRef.current = false;
  }, [open, product?.id, editMode, initialTab]);

  useEffect(() => {
    if (!shouldAutoSwitchToExportsRef.current || editMode || hideGeneratedMockupsTab) return;
    if (generatingMockup) return;
    if (!(savedMockups.length > 0 || mockupOutputs?.[preferredSize])) return;
    setActiveTab("exports");
    shouldAutoSwitchToExportsRef.current = false;
  }, [editMode, generatingMockup, hideGeneratedMockupsTab, savedMockups.length, mockupOutputs, preferredSize]);

  useEffect(() => {
    if (controlledTab) setActiveTab(controlledTab);
  }, [controlledTab]);

  useEffect(() => {
    if (!logoPlacement.logoUrl) return;
    const stillExists = uploadedLogos.some(
      (logo) => logo.logo_url === logoPlacement.logoUrl,
    );
    if (!stillExists) {
      logoPlacement.handleLogoUrl(null);
    }
  }, [uploadedLogos, logoPlacement.logoUrl, logoPlacement]);

  const hasNoPsds = Boolean(product?.id) && !loadingPsd && psdTemplates.length === 0;
  const showUploadTab = hideGeneratedMockupsTab || editMode;
  const showGenerateTab = true;
  const showExportsTab = !hideGeneratedMockupsTab && !editMode;
  const showTabs =
    (showGenerateTab ? 1 : 0) + (showUploadTab ? 1 : 0) + (showExportsTab ? 1 : 0) >
    1;
  const resolvedTab = controlledTab ?? activeTab;
  const showUploadContent = showUploadTab && resolvedTab === "upload";
  const showGenerateContent = showGenerateTab && resolvedTab === "generate";
  const isBusy =
    generatingMockup ||
    Boolean(editMode && editMockupId && updatingMockupId === editMockupId);
  const busyMessage =
    editMode && editMockupId && updatingMockupId === editMockupId
      ? ""
      : generatingMockup && activeTab === "upload"
        ? ""
        : "";
  const isCompactUploadDialog =
    showUploadContent && !editMode && (hideGeneratedMockupsTab || embedded);

  const psdTemplateOptions = useMemo(() => {
    const base = sortedPsdTemplates
      .filter((p) => p.id != null)
      .map((psd) => ({
        id: String(psd.id!),
        value: psd.placement_type_name ?? `Template ${psd.id}`,
      }));
    return [{ id: "", value: "Choose a PSD view..." }, ...base];
  }, [sortedPsdTemplates]);

  const handleManualUploadSave = useCallback(async () => {
    if (!manualUploadFiles?.length) {
      onError("Image required", "Please select an image to upload.");
      return;
    }
    if (editMode && editMockupId) {
      if (!onManualUploadMockup) return;
      await onManualUploadMockup({ id: editMockupId, file: manualUploadFiles[0] });
    } else if (hideGeneratedMockupsTab && onUploadMockupFiles) {
      await onUploadMockupFiles(manualUploadFiles);
    } else {
      onError("No mockup", "Missing mockup id for manual upload.");
      return;
    }
    setManualUploadFiles([]);
  }, [
    editMockupId,
    editMode,
    hideGeneratedMockupsTab,
    manualUploadFiles,
    onManualUploadMockup,
    onUploadMockupFiles,
  ]);

  const removeManualUploadPreview = useCallback((image: { title?: string }) => {
    setManualUploadFiles((prev) => {
      if (!prev.length) return prev;
      const index = prev.findIndex((file) => file.name === image?.title);
      if (index < 0) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleGenerateMockup = useCallback(async () => {
    const selectedLogoUrl = logoPlacement.getSelectedLogoUrl();
    if (!selectedLogoUrl) {
      return onError("No logo", "Please upload to library and select a logo first.");
    }
    if (!selectedPsdId || !selectedPsdTemplate) {
      return onError("No template", "Please select a PSD template.");
    }
    const coords = logoPlacement.getCanvasCoords(
      selectedPsdTemplate.canvas_w ?? 800,
      selectedPsdTemplate.canvas_h ?? 600,
    );
    if (!coords) return onError("Error", "Could not compute logo coordinates.");
    if (!hideGeneratedMockupsTab) {
      shouldAutoSwitchToExportsRef.current = true;
    }
    await onGenerateMockup({
      psd_template_id: selectedPsdId,
      logoUrl: selectedLogoUrl,
      x: coords.x,
      y: coords.y,
      width: coords.width,
      height: coords.height,
      rotation: coords.rotation,
    });
  }, [hideGeneratedMockupsTab, logoPlacement, onGenerateMockup, selectedPsdId, selectedPsdTemplate]);

  const handleCopyMockupUrl = useCallback((url: string) => {
    void navigator.clipboard.writeText(url);
  }, []);

  async function downloadMockupAsset(url: string, filename: string) {
    const directLink = document.createElement("a");
    directLink.href = url;
    directLink.download = filename;
    directLink.target = "_blank";
    directLink.rel = "noopener noreferrer";
    directLink.style.display = "none";
    document.body.appendChild(directLink);
    directLink.click();
    directLink.remove();
  }

  const contentSx = {
    minHeight: embedded ? 0 : isCompactUploadDialog ? undefined : { xs: 540, md: GENERATOR_MODAL_MIN_HEIGHT - 72 },
    height: embedded ? "100%" : undefined,
    flex: embedded ? 1 : undefined,
    display: "flex",
    flexDirection: "column" as const,
    pt: isCompactUploadDialog ? 2 : embedded ? 0 : undefined,
    pb: isCompactUploadDialog ? 2 : embedded ? 0 : undefined,
    px: isCompactUploadDialog ? 3 : embedded ? 0.5 : undefined,
  };

  const modalBody = (
    <>
      {showTabs && !hideInternalTabs && (
        <Tabs
          value={activeTab}
          onChange={(_, value) =>
            setActiveTab(value as "generate" | "exports" | "upload")
          }
          sx={{ mb: 2 }}
        >
          {showGenerateTab && <Tab value="generate" label="Generate" />}
          {showUploadTab ? (
            <Tab value="upload" label="Upload" />
          ) : (
            <Tab value="exports" label="Generated mockups" />
          )}
        </Tabs>
      )}

      {showGenerateContent && (
        <Box
          sx={
            embedded
              ? {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "hidden",
              }
              : undefined
          }
        >
          <>
            {hasNoPsds && (
              <Box
                sx={{
                  mt: 0,
                  flex: 1,
                  minHeight: embedded ? "100%" : undefined,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmptyStatePanel
                  title="No PSD Templates"
                  message="No PSD templates are configured for this product."
                  minHeight={0}
                />
              </Box>
            )}

            {sortedPsdTemplates?.length > 0 && (
              <Box
                sx={{
                  p: embedded ? 1.5 : 2,
                  borderRadius: 2,
                  ...(embedded && {
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }),
                }}
              >
                <FormTitle
                  title="Template & logo"
                  marginBottom={embedded ? 1 : 2}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    mt: embedded ? 0 : 2,
                    width: "100%",
                  }}
                >
                  {previewImageUrl && (
                    <Stack
                      direction={{ xs: "column", md: "row-reverse" }}
                      spacing={2}
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        alignItems: "stretch",
                        justifyContent: { md: "space-between" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            borderRadius: 2,
                            bgcolor: "grey.100",
                            borderColor: "divider",
                            width: "fit-content",
                            maxWidth: "100%",
                            ml: { md: "auto" },
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1 }}>
                            <CustomTypography variant="caption" fontWeight={700}>
                              Zoom
                            </CustomTypography>
                            <IconButton
                              size="small"
                              aria-label="Zoom out"
                              onClick={() => setEditorZoom((z) => Math.max(1, Number((z - 0.1).toFixed(2))))}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Slider
                              size="small"
                              min={1}
                              max={4}
                              step={0.1}
                              value={editorZoom}
                              onChange={(_, value) => setEditorZoom(value as number)}
                              sx={{ width: 120 }}
                            />
                            <IconButton
                              size="small"
                              aria-label="Zoom in"
                              onClick={() => setEditorZoom((z) => Math.min(4, Number((z + 0.1).toFixed(2))))}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <CustomTypography variant="caption" color="text.secondary" sx={{ minWidth: 42 }}>
                              {Math.round(editorZoom * 100)}%
                            </CustomTypography>
                          </Stack>
                          <Box
                            sx={{
                              position: "relative",
                              width: DISPLAY_MAX_WIDTH,
                              height: DISPLAY_MAX_HEIGHT,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              overflow: "auto",
                              bgcolor: "common.white",
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: DISPLAY_MAX_WIDTH * editorZoom,
                                height: DISPLAY_MAX_HEIGHT * editorZoom,
                              }}
                            >
                              <Box
                                ref={logoPlacement.previewRef}
                                onDrop={logoPlacement.handleDrop}
                                onDragOver={logoPlacement.handleDragOver}
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  width: DISPLAY_MAX_WIDTH,
                                  height: DISPLAY_MAX_HEIGHT,
                                  transform: `scale(${editorZoom})`,
                                  transformOrigin: "top left",
                                  overflow: "hidden",
                                  userSelect: "none",
                                }}
                              >
                                <img
                                  src={previewImageUrl}
                                  alt="PSD Preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    pointerEvents: "none",
                                    opacity: loadingPlacementPreview ? 0.7 : 1,
                                  }}
                                  draggable={false}
                                />
                                {logoPlacement.logoUrl && (
                                  <>
                                    {logoPlacement.printAreaBounds ? (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          left: logoPlacement.printAreaBounds.left,
                                          top: logoPlacement.printAreaBounds.top,
                                          width: logoPlacement.printAreaBounds.width,
                                          height: logoPlacement.printAreaBounds.height,
                                          overflow: "hidden",
                                          border: "1px dashed",
                                          borderColor: "primary.light",
                                          borderRadius: 0.5,
                                          zIndex: 9,
                                        }}
                                      >
                                        <Box
                                          ref={logoPlacement.logoRef}
                                          onMouseDown={logoPlacement.handleDragStart}
                                          sx={{
                                            position: "absolute",
                                            left:
                                              logoPlacement.logoTransform.x -
                                              logoPlacement.printAreaBounds.left,
                                            top:
                                              logoPlacement.logoTransform.y -
                                              logoPlacement.printAreaBounds.top,
                                            width: logoPlacement.logoTransform.width,
                                            height: logoPlacement.logoTransform.height,
                                            cursor: logoPlacement.isDragging ? "grabbing" : "grab",
                                            border: "2px solid",
                                            borderColor: "primary.main",
                                            boxSizing: "border-box",
                                            transform: `rotate(${logoPlacement.logoTransform.rotation}deg)`,
                                            transformOrigin: "center center",
                                            zIndex: 10,
                                            userSelect: "none",
                                          }}
                                        >
                                          <img
                                            src={logoPlacement.logoUrl}
                                            alt="Logo"
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "contain",
                                              pointerEvents: "none",
                                              userSelect: "none",
                                            }}
                                            draggable={false}
                                          />
                                          <Box
                                            onMouseDown={logoPlacement.handleResizeStart}
                                            sx={{
                                              position: "absolute",
                                              right: -6,
                                              bottom: -6,
                                              width: 14,
                                              height: 14,
                                              backgroundColor: "primary.main",
                                              borderRadius: "50%",
                                              cursor: "nwse-resize",
                                              border: "2px solid white",
                                              zIndex: 11,
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <Box
                                        ref={logoPlacement.logoRef}
                                        onMouseDown={logoPlacement.handleDragStart}
                                        sx={{
                                          position: "absolute",
                                          left: logoPlacement.logoTransform.x,
                                          top: logoPlacement.logoTransform.y,
                                          width: logoPlacement.logoTransform.width,
                                          height: logoPlacement.logoTransform.height,
                                          cursor: logoPlacement.isDragging ? "grabbing" : "grab",
                                          border: "2px solid",
                                          borderColor: "primary.main",
                                          boxSizing: "border-box",
                                          transform: `rotate(${logoPlacement.logoTransform.rotation}deg)`,
                                          transformOrigin: "center center",
                                          zIndex: 10,
                                          userSelect: "none",
                                        }}
                                      >
                                        <img
                                          src={logoPlacement.logoUrl}
                                          alt="Logo"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                            pointerEvents: "none",
                                            userSelect: "none",
                                          }}
                                          draggable={false}
                                        />
                                        <Box
                                          onMouseDown={logoPlacement.handleResizeStart}
                                          sx={{
                                            position: "absolute",
                                            right: -6,
                                            bottom: -6,
                                            width: 14,
                                            height: 14,
                                            backgroundColor: "primary.main",
                                            borderRadius: "50%",
                                            cursor: "nwse-resize",
                                            border: "2px solid white",
                                            zIndex: 11,
                                          }}
                                        />
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {previewImageUrl && (
                          <Box
                            sx={{
                              mt: embedded ? 1 : 2,
                              display: "flex",
                              justifyContent: { xs: "center", md: "flex-end" },
                              gap: 2,
                              flexShrink: 0,
                            }}
                          >
                            <AppButton
                              label="Reset"
                              variant="secondary"
                              onClick={() => logoPlacement.handleLogoUrl(null)}
                              disabled={!logoPlacement.logoUrl || generatingMockup}
                              customStyles={{ width: "120px" }}
                            />
                            <AppButton
                              label={generatingMockup ? "Generating..." : "Generate mockup"}
                              variant="primary"
                              onClick={handleGenerateMockup}
                              disabled={!logoPlacement.logoUrl || generatingMockup}
                              customStyles={{ width: "150px" }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          width: { xs: "100%", md: 320 },
                          minWidth: { md: 320 },
                          height: 520,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box sx={{ mb: 3 }}>
                          <AppDropDown
                            title="view"
                            isMultiSelect={false}
                            list={psdTemplateOptions}
                            value={selectedPsdId != null ? String(selectedPsdId) : ""}
                            onSingleSelectionChange={(id) => {
                              if (lockPsdSelection) return;
                              if (!id) return;
                              onSelectPsdId(Number(id));
                            }}
                            placeholderText="Choose a view..."
                            width="100%"
                            disabled={lockPsdSelection}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                          <CustomTypography variant="caption" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                            Uploaded logos
                          </CustomTypography>
                          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                            {loadingUploadedLogos ? (
                              <CustomTypography variant="caption" color="text.secondary">
                                Loading logos...
                              </CustomTypography>
                            ) : uploadedLogos.length === 0 ? (
                              <CustomTypography variant="caption" color="text.secondary">
                                No logos uploaded yet.
                              </CustomTypography>
                            ) : (
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))",
                                  gap: 1,
                                }}
                              >
                                {uploadedLogos?.map((logo) => (
                                  <Box
                                    key={logo.id}
                                    className="logo-card"
                                    sx={{
                                      position: "relative",
                                      height: 92,
                                      border: "2px solid",
                                      borderColor:
                                        logoPlacement.logoUrl === logo.logo_url
                                          ? "primary.main"
                                          : "divider",
                                      borderRadius: 2,
                                      backgroundColor: "grey.50",
                                      p: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      overflow: "hidden",
                                      "& .logo-card-actions": { opacity: 0 },
                                      "&:hover .logo-card-actions": { opacity: 1 },
                                      "&:hover": { borderColor: "primary.light" },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={logo.logo_url}
                                      alt="Uploaded logo"
                                      sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                    />
                                    <Stack
                                      className="logo-card-actions"
                                      direction="row"
                                      spacing={0.5}
                                      sx={{
                                        position: "absolute",
                                        inset: 0,
                                        transition: "opacity 120ms ease",
                                        bgcolor: "rgba(0, 0, 0, 0.38)",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <IconButton
                                        size="small"
                                        aria-label="Select uploaded logo"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          logoPlacement.handleLogoUrl(logo.logo_url);
                                        }}
                                        sx={{
                                          bgcolor: "background.paper",
                                          "&:hover": { bgcolor: "grey.100" },
                                        }}
                                      >
                                        <CheckCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        aria-label="Delete uploaded logo"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (logoPlacement.logoUrl === logo.logo_url) {
                                            logoPlacement.handleLogoUrl(null);
                                          }
                                          void onDeleteUploadedLogo(logo.id);
                                        }}
                                        sx={{
                                          bgcolor: "background.paper",
                                          "&:hover": { bgcolor: "grey.100" },
                                        }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <AppButton
                            label="Upload logo"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => logoLibraryInputRef.current?.click()}
                            customStyles={{ width: "100%", marginTop: "16px" }}
                          />
                          <input
                            ref={logoLibraryInputRef}
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp,.svg"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0] ?? null;
                              if (file) {
                                void onUploadLogoToLibrary(file);
                              }
                              e.currentTarget.value = "";
                            }}
                          />
                        </Box>
                      </Paper>
                    </Stack>
                  )}
                </Box>

                {previewImageUrl && logoPlacement.logoUrl && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, width: { xs: "100%", md: 320 }, marginTop: "-20px" }}
                  >
                    <CustomTypography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0 }}>
                      Rotation
                    </CustomTypography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                      <IconButton size="small" onClick={() => logoPlacement.rotate(-15)}>
                        <RotateLeftIcon fontSize="small" />
                      </IconButton>
                      <Slider
                        size="small"
                        value={logoPlacement.logoTransform.rotation}
                        min={-180}
                        max={180}
                        onChange={(_, v) => logoPlacement.setRotation(v as number)}
                        valueLabelDisplay="auto"
                        sx={{ flex: 1 }}
                      />
                      <IconButton size="small" onClick={() => logoPlacement.rotate(15)}>
                        <RotateRightIcon fontSize="small" />
                      </IconButton>
                      <CustomTypography variant="caption" sx={{ minWidth: 36 }}>
                        {logoPlacement.logoTransform.rotation}deg
                      </CustomTypography>
                    </Stack>
                    <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Logo size (current): {Math.round(logoPlacement.logoTransform.width)} x{" "}
                      {Math.round(logoPlacement.logoTransform.height)} px
                    </CustomTypography>
                    {logoPlacement.logoOriginalSize && (
                      <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Original: {logoPlacement.logoOriginalSize.width || "-"} x{" "}
                        {logoPlacement.logoOriginalSize.height || "-"} px
                      </CustomTypography>
                    )}
                  </Paper>
                )}

              </Box>
            )}
          </>
        </Box>
      )}

      {showUploadContent && (
        <Box
          sx={{
            mt: isCompactUploadDialog ? 1 : 2,
            display: "flex",
            flexDirection: "column",
            ...(embedded && { flex: 1, minHeight: 0, height: "100%" }),
          }}
        >
          {!isCompactUploadDialog && <Divider sx={{ mb: 2 }} />}
          {/* {isCompactUploadDialog && (
              <CustomTypography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {editMode
                  ? "Choose an image to replace this mockup."
                  : "Select one or more mockup images to upload."}
              </CustomTypography>
            )} */}
          <Box
            sx={
              embedded
                ? {
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }
                : undefined
            }
          >
            <ImageUpload
              id={`modal-manual-upload-${String(product?.id || "")}-${String(editMockupId || "new")}`}
              size={isCompactUploadDialog ? "medium" : "large"}
              previewImage
              showImagesList={!editMode && hideGeneratedMockupsTab}
              multiple={!editMode && hideGeneratedMockupsTab}
              existingImageList={[]}
              newImageList={manualUploadFiles}
              minHeight={
                embedded ? "100%" : isCompactUploadDialog ? "280px" : "420px"
              }
              minInputWidth="100%"
              minWidth="100%"
              uploadText={
                isCompactUploadDialog ? "Drop mockup images here" : undefined
              }
              disabled={
                generatingMockup ||
                (editMode && updatingMockupId === editMockupId)
              }
              addImagesToBeUploaded={(files) => setManualUploadFiles(files ?? [])}
              deleteImage={removeManualUploadPreview}
              removeImage={removeManualUploadPreview}
            />
          </Box>
          <Box
            sx={{
              mt: isCompactUploadDialog ? 2.5 : "auto",
              pt: isCompactUploadDialog ? 2 : 2,
              pb: isCompactUploadDialog ? 0.5 : 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {isCompactUploadDialog && !embedded && (
              <AppButton
                label="Cancel"
                variant="grayscale"
                onClick={onClose}
                disabled={isBusy}
                customStyles={{ width: "120px" }}
              />
            )}
            <AppButton
              label={
                editMode && updatingMockupId === editMockupId
                  ? "Saving..."
                  : generatingMockup
                    ? "Uploading..."
                    : isCompactUploadDialog
                      ? editMode
                        ? "Replace mockup"
                        : "Upload mockups"
                      : "Save upload"
              }
              variant="primary"
              onClick={() => void handleManualUploadSave()}
              disabled={
                !manualUploadFiles?.length ||
                generatingMockup ||
                (editMode && updatingMockupId === editMockupId)
              }
              customStyles={{ width: isCompactUploadDialog ? "160px" : "150px" }}
            />
          </Box>
        </Box>
      )}

      {!editMode && activeTab === "exports" && (savedMockups.length > 0 || mockupOutputs?.[preferredSize]) && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <FormTitle title="Exports" marginBottom={1} />

          {savedMockups.length > 0 ? (
            <>
              <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Saved mockups (recommended size: {preferredSize})
              </CustomTypography>
              <Box sx={{ width: "100%" }}>
                <Grid2 container spacing={2}>
                  {savedMockups.map((m) => {
                    const urls = deriveMockupOutputsFromStoredUrl(m.mockup_url);
                    const url = urls[preferredSize];
                    const filename = `${preferredSize}.webp`;
                    const created = m.created_at ? String(m.created_at).slice(0, 19) : "";
                    return (
                      <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={m.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            height: "100%",
                            minHeight: 300,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <CustomTypography variant="caption" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                            {m.reference_id ? `Ref: ${m.reference_id}` : `Export #${m.id}`}
                          </CustomTypography>
                          {created && (
                            <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                              {created}
                            </CustomTypography>
                          )}
                          <MockupExportPreview
                            src={url}
                            alt={`${preferredSize} mockup`}
                            fixedHeight={EXPORT_CARD_PREVIEW_HEIGHT}
                          />
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <IconButton size="small" aria-label="Download" onClick={() => downloadMockupAsset(url, filename)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label="Copy URL" onClick={() => handleCopyMockupUrl(url)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" aria-label="Open">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete"
                              onClick={() => void onDeleteMockup(m.id)}
                              disabled={deletingGeneratedMockupId === m.id}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Paper>
                      </Grid2>
                    );
                  })}
                </Grid2>
              </Box>
            </>
          ) : (
            mockupOutputs?.[preferredSize] && (
              <Box>
                {mockupReferenceId && (
                  <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Reference: {mockupReferenceId}
                  </CustomTypography>
                )}
                <Box sx={{ width: "100%" }}>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                        <CustomTypography variant="caption" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                          {preferredSize}
                        </CustomTypography>
                        <MockupExportPreview
                          src={mockupOutputs?.[preferredSize] as string}
                          alt={`${preferredSize} mockup`}
                          fixedHeight={EXPORT_CARD_PREVIEW_HEIGHT}
                        />
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <IconButton
                            size="small"
                            aria-label="Download"
                            onClick={() => downloadMockupAsset(mockupOutputs?.[preferredSize] as string, `${preferredSize}.webp`)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" aria-label="Copy URL" onClick={() => handleCopyMockupUrl(mockupOutputs?.[preferredSize] as string)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            component="a"
                            href={mockupOutputs?.[preferredSize] as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Paper>
                    </Grid2>
                  </Grid2>
                </Box>
              </Box>
            )
          )}
        </Box>
      )}
      {!editMode && activeTab === "exports" && !(savedMockups.length > 0 || mockupOutputs?.[preferredSize]) && (
        <Box
          sx={{
            mt: 0,
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <EmptyStatePanel
            title="No Generated Mockups"
            message="No generated mockups available yet."
            minHeight={0}
          />
        </Box>
      )}
    </>
  );

  const busyOverlay = isBusy ? (
    <Backdrop
      open
      sx={{
        position: "absolute",
        zIndex: (theme) => theme.zIndex.modal + 1,
        color: "#fff",
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        borderRadius: "inherit",
      }}
    >
      <Stack alignItems="center" spacing={2}>
        <CircularProgress color="inherit" />
        <CustomTypography variant="body2" sx={{ color: "inherit" }}>
          {busyMessage}
        </CustomTypography>
      </Stack>
    </Backdrop>
  ) : null;

  if (embedded) {
    if (!open) return null;
    return (
      <Box sx={{ position: "relative", width: "100%", ...contentSx }}>
        {modalBody}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isBusy) return;
        onClose();
      }}
      maxWidth={isCompactUploadDialog ? "md" : "lg"}
      fullWidth
      PaperProps={{
        sx: {
          position: "relative",
          maxWidth: isCompactUploadDialog ? 720 : 1120,
          minHeight: isCompactUploadDialog
            ? undefined
            : { xs: 620, md: GENERATOR_MODAL_MIN_HEIGHT },
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          pb: isCompactUploadDialog ? 1 : undefined,
          ...(isCompactUploadDialog && {
            fontSize: "1rem",
            fontWeight: 600,
            lineHeight: 1.4,
          }),
        }}
      >
        {isCompactUploadDialog
          ? `Upload Mockups${product?.label ? ` - ${product.label}` : ""}`
          : `Mockup Generator${product?.label ? ` - ${product.label}` : ""}`}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={isBusy}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={contentSx}>
        {modalBody}
      </DialogContent>
      {busyOverlay}
    </Dialog>
  );
};

export default observer(MockupGeneratorModal);
